// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild, ElementRef, Renderer2 } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { DropdownComponent } from '../../../shared/dropdown/dropdown.component';
import { ButtonsComponent } from '../../../shared/buttons/buttons.component';
import { TableSearchComponent } from '../../../shared/table-search/table-search.component';
import { IconsComponent } from '../../../shared/icons/icons.component';
import { HceFileManagementService } from '../../../services/hce/hce-file-management/hce-file-management.service';

export interface ReportFile {
  fileName: string;
  fileType: string;
  sizeBytes: number;
  path?: string;
}

export interface HceReportsModalData {
  records: ReportFile[];
  fileTypeOptions?: string[];
  sampleName?: string;
  sampleId?: string | number;
  analysisId?: number;
  loading?: boolean;
}

type ColumnKey = 'fileName' | 'fileType' | 'size' | 'actions';

@Component({
  selector: 'app-hce-reports-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    DropdownComponent,
    ButtonsComponent,
    TableSearchComponent,
    IconsComponent,
  ],
  templateUrl: './hce-reports-modal.component.html',
  styleUrls: ['./hce-reports-modal.component.css'],
})
export class HceReportsModalComponent implements OnInit, OnDestroy, AfterViewInit {

  private destroy$ = new Subject<void>();
  displayedColumns: ColumnKey[] = [
    'fileName',
    'fileType',
    'size',
    'actions',
  ];
  dataSource = new MatTableDataSource<ReportFile>([]);
  rawRecords: ReportFile[] = [];
  fileTypeCtl = new FormControl<string>('all', { nonNullable: true });
  lastSearch = '';
  fileTypeOptions: string[] = [];
  dropdownOptions: Array<{ value: string; label: string }> = [];
  loading = true;
  private placeholderText = 'Search (File Name)';
  private placeholderPatched = false;
  private mo?: MutationObserver;

  @ViewChild(MatPaginator, { static: false }) paginator?: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort?: MatSort;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: HceReportsModalData,
    private dialogRef: MatDialogRef<HceReportsModalComponent>,
    private files: HceFileManagementService,
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,

  ) { }

  ngOnInit(): void {
    this.loading = !!this.data?.loading;
    this.fileTypeCtl.setValue('all');
    this.dataSource.filterPredicate = (rec, filter) => {
      const f = (filter ?? '').trim().toLowerCase();
      return (rec.fileName ?? '').toLowerCase().includes(f);
    };
    this.fileTypeCtl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.applyFilters();
    });
    // Import if there is initial data.
    if ((this.data?.records ?? []).length > 0) {
      this.setRecords(
        this.data.records,
        this.data.fileTypeOptions ?? []
      );
    }

    setTimeout(() => this.overwritePaginatorLabel(), 0);
  }

  ngAfterViewInit(): void {
    this.wireTableFeatures();
    this.patchSearchPlaceholder();
    this.observeSearchInput();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.mo) {
      this.mo.disconnect();
    }
  }

  setRecords(records: ReportFile[], fileTypeOptions?: string[]): void {
    this.rawRecords = [...records];

    if (fileTypeOptions) {
      this.fileTypeOptions = [...fileTypeOptions];
      this.buildDropdownOptions();
    }

    this.loading = false;

    // Connect paginator/sort after template rendering when loading is released.
    setTimeout(() => {
      this.wireTableFeatures();
      this.applyFilters();
      this.overwritePaginatorLabel();
    }, 0);
  }

  onSearchChanged(value: string): void {
    this.lastSearch = value;
    this.applyFilters();
  }

  private wireTableFeatures(): void {
    if (this.paginator) {
      this.dataSource.paginator = this.paginator;
    }

    if (this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  private buildDropdownOptions(): void {
    const head = { value: 'all', label: $localize`All` };
    const rest = this.fileTypeOptions.map(t => ({ value: t, label: t }));
    this.dropdownOptions = [head, ...rest];
  }

  private overwritePaginatorLabel(): void {
    const intl: MatPaginatorIntl = (this.paginator as any)?._intl;
    if (!intl) return;
    intl.itemsPerPageLabel = $localize`Rows per page:`;
    intl.nextPageLabel = $localize`Next page`;
    intl.previousPageLabel = $localize`Previous page`;
    intl.firstPageLabel = $localize`First page`;
    intl.lastPageLabel = $localize`Last page`;
    intl.getRangeLabel = (page, pageSize, length) => {
      if (length === 0 || pageSize === 0) return $localize`0 of ${length}`;
      const startIndex = page * pageSize;
      const endIndex = Math.min(startIndex + pageSize, length);
      return $localize`${startIndex + 1} – ${endIndex} of ${length}`;
    };
    intl.changes.next();
  }

  private applyFilters(): void {
    const selectedType = this.fileTypeCtl.value ?? 'all';
    const byType = selectedType === 'all'
      ? this.rawRecords
      : this.rawRecords.filter(r => (r.fileType ?? '') === selectedType);

    this.dataSource.data = byType;
    this.dataSource.filter = this.lastSearch;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  formatBytes(n?: number | null): string {
    if (n == null || isNaN(n)) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
  }

  onDownloadIconClick(r: ReportFile): void {
    if (!r?.path) return;

    const path = r.path;
    const isCommon = path.startsWith('refs/');
    const analysisId = (this.data as any)?.analysisId;

    const downloadWithCustomName = (signedUrl: string, originalName: string) => {
      const newName = `${analysisId}_${originalName}`;

      fetch(signedUrl)
        .then(res => res.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = newName;
          a.click();
          URL.revokeObjectURL(blobUrl);
        })
        .catch(err => console.error('Download blob error:', err));
    };

    if (isCommon) {
      this.files.getCommonSignedUrl(path).subscribe({
        next: signedUrl => downloadWithCustomName(signedUrl, r.fileName),
        error: e => console.error('[download] common failed:', e)
      });
      return;
    }

    if (typeof analysisId !== 'number') {
      console.warn('[download] missing analysisId');
      return;
    }

    this.files.getAnalysisSignedUrl(analysisId, path).subscribe({
      next: signedUrl => downloadWithCustomName(signedUrl, r.fileName),
      error: e => console.error('[download] analysis failed:', e)
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  private patchSearchPlaceholder(): void {
    if (this.placeholderPatched) return;
    const root: HTMLElement = this.host.nativeElement;
    const input: HTMLInputElement | null = root.querySelector(
      '.filters app-table-search input.mat-mdc-input-element'
    );
    if (input) {
      this.renderer.setAttribute(input, 'placeholder', this.placeholderText);
      this.placeholderPatched = true;
    } else {
      setTimeout(() => this.patchSearchPlaceholder(), 50);
    }
  }

  private observeSearchInput(): void {
    const root: HTMLElement = this.host.nativeElement;
    const target = root.querySelector('.filters app-table-search');
    if (!target) return;

    this.mo = new MutationObserver(() => {
      this.placeholderPatched = false;
      this.patchSearchPlaceholder();
    });
    this.mo.observe(target, { childList: true, subtree: true });
  }

}
